/**
 * Admin · Proposal Presets / Estimate Templates (Phase 2L-A27 Step 2)
 *
 * Reusable estimate templates that admins build once and apply to
 * proposals on demand. Step 2 is the MANAGEMENT page only — the
 * "Add from preset" button inside the proposal builder is Step 3
 * and is intentionally NOT touched here.
 *
 * Mirrors the proposal builder UX (LineEditor + OptionGroupsEditor are
 * reused as-is) so admins don't have to learn a second shape.
 *
 * Route: /admin/proposal-presets
 * API:   /api/admin/proposal-presets (CRUD)
 *
 * Backend enforces validation; the frontend mirrors the most common
 * rules to give nicer toast feedback before the POST/PATCH.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, AlertCircle, Power, PowerOff, Search } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { formatDateTimeAU } from "@/lib/formatDateAU";
import { LineEditor, OptionGroupsEditor } from "@/pages/admin/Proposals";

const money = (cents) => {
  const amount = (cents || 0) / 100;
  return new Intl.NumberFormat("en-AU", {
    style: "currency", currency: "AUD", currencyDisplay: "narrowSymbol",
  }).format(amount);
};

const LINE_STATUSES = [
  { value: "required",    label: "Required" },
  { value: "recommended", label: "Recommended" },
  { value: "optional",    label: "Optional" },
];

const VALID_TEST_TYPES = ["tank", "bore", "basic"];
const VALID_WATER_SOURCES = ["", "tank", "bore", "either"];


// ============================================================ PRESET DETAIL DRAWER

function PresetDetail({ presetId, products, onClose, onChanged }) {
  // `presetId === "new"` is the create sentinel; a real uuid means edit.
  const isCreate = presetId === "new";

  function blankForm() {
    return {
      name: "",
      description: "",
      category: "",
      sort_order: 100,
      active: true,
      option_groups: [],
      lines: [],
      // Future recommendation fields (stored but unused by the engine yet)
      recommendation_keys: [],
      applies_to_test_types: [],
      water_source: "",
      severity_minimum: "",
      default_for_recommendation: false,
    };
  }

  // For create: form starts populated; for edit: null until loaded.
  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState(() => isCreate ? blankForm() : null);
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (isCreate) return;
    let cancelled = false;
    api.get(`/admin/proposal-presets/${presetId}`).then(({ data }) => {
      if (cancelled) return;
      setDoc(data);
      setForm({
        name: data.name || "",
        description: data.description || "",
        category: data.category || "",
        sort_order: data.sort_order ?? 100,
        active: data.active !== false,
        option_groups: (data.option_groups || []).map((g) => ({
          id: g.id, label: g.label || "", description: g.description || "",
          recommended_badge_text: g.recommended_badge_text || "Recommended",
          default_product_id: g.default_product_id || null,
        })),
        lines: (data.lines || []).map((l) => ({
          product_id: l.product_id, qty: l.qty ?? 1,
          line_status: l.line_status || "optional",
          notes: l.notes || null,
          option_group_id: l.option_group_id || null,
        })),
        recommendation_keys: data.recommendation_keys || [],
        applies_to_test_types: data.applies_to_test_types || [],
        water_source: data.water_source || "",
        severity_minimum: data.severity_minimum || "",
        default_for_recommendation: !!data.default_for_recommendation,
      });
    }).catch((e) => toast.error(formatApiError(e)));
    return () => { cancelled = true; };
  }, [presetId, isCreate, reloadKey]);

  // Mirror the backend's "starting total preview" rule — required +
  // recommended (non-group) + the default option per group.
  const subtotal = useMemo(() => {
    if (!form) return 0;
    const lines = form.lines || [];
    const groups = form.option_groups || [];
    const groupDefault = {};
    for (const g of groups) {
      const members = lines.filter((l) => l.option_group_id === g.id && l.product_id);
      if (!members.length) continue;
      if (g.default_product_id && members.some((m) => m.product_id === g.default_product_id)) {
        groupDefault[g.id] = g.default_product_id;
      } else {
        const ranked = members
          .map((m) => ({ pid: m.product_id, price: products.find((p) => p.id === m.product_id)?.unit_price_cents || 0 }))
          .sort((a, b) => b.price - a.price);
        groupDefault[g.id] = ranked[0]?.pid;
      }
    }
    let total = 0;
    for (const l of lines) {
      if (!l.product_id) continue;
      const unit = products.find((p) => p.id === l.product_id)?.unit_price_cents || 0;
      const lt = unit * (Number(l.qty) || 0);
      const status = l.line_status || "optional";
      if (status === "required") total += lt;
      else if (l.option_group_id) {
        if (groupDefault[l.option_group_id] === l.product_id) total += lt;
      } else if (status === "recommended") total += lt;
    }
    return total;
  }, [form, products]);

  const updateLine = (idx, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l, i) => i === idx ? patch : l) }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { product_id: "", qty: 1, line_status: "optional", option_group_id: null }] }));
  const removeLine = (idx) => setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));
  const updateGroups = (groups) => setForm((f) => ({ ...f, option_groups: groups }));

  // Frontend mirror of the backend's _validate_preset rules. Backend remains
  // the source of truth — this is purely for nicer toast UX before the call.
  const validate = () => {
    if (!form.name || !form.name.trim()) return "Preset name is required.";
    const groups = form.option_groups || [];
    const groupIds = new Set();
    for (const g of groups) {
      if (!g.label || !g.label.trim()) return "Every option group needs a label.";
      if (groupIds.has(g.id)) return "Duplicate option group id.";
      groupIds.add(g.id);
      const members = (form.lines || []).filter((l) => l.option_group_id === g.id && l.product_id);
      if (members.length < 2) {
        return `Option group "${g.label}" needs at least 2 options (got ${members.length}).`;
      }
      const bad = members.find((m) => (m.line_status || "optional") !== "optional");
      if (bad) return `Option group "${g.label}" contains a ${bad.line_status} line. Only Optional lines can belong to a group.`;
      const pids = members.map((m) => m.product_id);
      if (new Set(pids).size !== pids.length) return `Option group "${g.label}" has the same product listed twice.`;
      if (g.default_product_id && !pids.includes(g.default_product_id)) {
        return `Option group "${g.label}" default option must be one of the assigned products.`;
      }
    }
    const validGroupIds = new Set(groups.map((g) => g.id));
    for (const l of form.lines || []) {
      if (l.option_group_id && !validGroupIds.has(l.option_group_id)) {
        return `A line references option group "${l.option_group_id}" which doesn't exist.`;
      }
      if (l.product_id && (Number(l.qty) || 0) <= 0) {
        return "Every line qty must be greater than 0.";
      }
    }
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const cleanLines = form.lines
        .filter((l) => l.product_id)
        .map((l) => ({
          ...l,
          option_group_id: l.line_status === "optional" ? (l.option_group_id || null) : null,
        }));
      const body = {
        name: form.name.trim(),
        description: form.description || null,
        category: form.category || null,
        sort_order: form.sort_order,
        option_groups: form.option_groups || [],
        lines: cleanLines,
        recommendation_keys: form.recommendation_keys,
        applies_to_test_types: form.applies_to_test_types,
        water_source: form.water_source || null,
        severity_minimum: form.severity_minimum || null,
        default_for_recommendation: !!form.default_for_recommendation,
      };
      if (isCreate) {
        const { data } = await api.post(`/admin/proposal-presets`, body);
        toast.success("Preset created");
        onChanged?.(data.id);
      } else {
        await api.patch(`/admin/proposal-presets/${doc.id}`, body);
        toast.success("Preset saved");
        setReloadKey((k) => k + 1);
        onChanged?.(doc.id);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    if (!doc) return;
    try {
      if (doc.active === false) {
        // Reactivate via PATCH
        await api.patch(`/admin/proposal-presets/${doc.id}`, { active: true });
        toast.success("Preset reactivated");
      } else {
        await api.delete(`/admin/proposal-presets/${doc.id}`);
        toast.success("Preset deactivated");
      }
      setReloadKey((k) => k + 1);
      onChanged?.(doc.id);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!form) {
    return (
      <div data-testid="preset-detail-drawer" className="fixed inset-y-0 right-0 z-40 w-full lg:w-[760px] bg-slate-50 border-l border-slate-200 shadow-2xl overflow-y-auto">
        <div className="p-6 text-sm text-slate-500">Loading…</div>
      </div>
    );
  }

  return (
    <div data-testid="preset-detail-drawer" className="fixed inset-y-0 right-0 z-40 w-full lg:w-[760px] bg-slate-50 border-l border-slate-200 shadow-2xl overflow-y-auto">
      <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">Estimate Template</div>
          <h2 className="font-display text-xl font-bold text-slate-900">
            {isCreate ? "New Preset" : (doc?.name || "Edit Preset")}
          </h2>
          {doc && (
            <div className="text-[11px] text-slate-500 mt-1">
              Created {doc.created_at ? formatDateTimeAU(doc.created_at) : ""} by {doc.created_by || "—"}
              {" · "} Updated {doc.updated_at ? formatDateTimeAU(doc.updated_at) : ""} by {doc.updated_by || "—"}
            </div>
          )}
        </div>
        <button data-testid="preset-detail-close" onClick={onClose} className="p-2 rounded-full hover:bg-slate-100">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-6 space-y-6">
        {/* Header fields */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Preset name *</Label>
              <Input
                data-testid="preset-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Whole Home Filtration — Tank Water Options"
                className="mt-1 h-9 text-sm"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Category</Label>
              <Input
                data-testid="preset-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder="e.g. Whole Home, Tank-Only"
                className="mt-1 h-9 text-sm"
              />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Description</Label>
            <Textarea
              data-testid="preset-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              placeholder="Optional — short summary admins see in the list."
              className="mt-1 text-sm"
            />
          </div>
          <div className="flex items-center gap-3">
            <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Sort order</Label>
            <Input
              data-testid="preset-sort-order"
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })}
              className="w-24 h-9 text-sm"
            />
            {doc && doc.active === false && (
              <span data-testid="preset-inactive-badge" className="inline-flex items-center gap-1 text-[11px] uppercase font-bold tracking-wider text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1 rounded-full">
                <PowerOff className="w-3 h-3" /> Inactive
              </span>
            )}
          </div>
        </div>

        {/* Option groups (reused from the proposal builder) */}
        <OptionGroupsEditor
          groups={form.option_groups || []}
          lines={form.lines || []}
          products={products}
          onChange={updateGroups}
          locked={false}
        />

        {/* Lines */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500">Preset lines</div>
            <Button variant="outline" data-testid="add-preset-line-btn" onClick={addLine} className="rounded-full" size="sm">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add item
            </Button>
          </div>
          <div className="space-y-2">
            {form.lines.length === 0 && (
              <div className="text-center py-6 text-sm text-slate-400">No items yet. Add at least one before saving.</div>
            )}
            {form.lines.map((line, i) => (
              <LineEditor
                key={i}
                line={line}
                index={i}
                products={products}
                optionGroups={form.option_groups || []}
                locked={false}
                onChange={(patch) => updateLine(i, patch)}
                onRemove={() => removeLine(i)}
              />
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
              Starting total preview (required + default options + recommended add-ons)
            </div>
            <div data-testid="preset-subtotal" className="text-xl font-bold tabular-nums text-slate-900">
              {money(subtotal)}
            </div>
          </div>
        </div>

        {/* Phase 2L-A33 — Recommendation mapping section.
            Currently stored as MANUAL mapping metadata only. The water-test
            recommendation engine does NOT auto-create estimates from these
            keys yet (locked under PRD). */}
        <div className="bg-slate-50 rounded-2xl border border-dashed border-slate-300 p-4 space-y-3">
          <div>
            <div
              data-testid="preset-rec-mapping-heading"
              className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500"
            >
              Recommendation mapping
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              These keys are stored for future auto-estimate creation. They do
              not automatically add products to reports or estimates yet.
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                Recommendation keys (comma-separated)
              </Label>
              <Input
                data-testid="preset-rec-keys"
                value={form.recommendation_keys.join(", ")}
                onChange={(e) => setForm({
                  ...form,
                  recommendation_keys: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                })}
                placeholder="uv_recommended, biocleanse_recommended"
                className="mt-1 h-9 text-sm"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Water source</Label>
              <select
                data-testid="preset-water-source"
                value={form.water_source}
                onChange={(e) => setForm({ ...form, water_source: e.target.value })}
                className="mt-1 w-full h-9 border border-slate-300 rounded-md px-2 text-sm"
              >
                {VALID_WATER_SOURCES.map((w) => (
                  <option key={w || "any"} value={w}>{w ? w : "— either / any —"}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                Applies to test types
              </Label>
              <div className="mt-1 flex gap-3 items-center">
                {VALID_TEST_TYPES.map((t) => (
                  <label key={t} className="inline-flex items-center gap-1 text-xs">
                    <input
                      type="checkbox"
                      data-testid={`preset-test-type-${t}`}
                      checked={form.applies_to_test_types.includes(t)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...form.applies_to_test_types, t]
                          : form.applies_to_test_types.filter((x) => x !== t);
                        setForm({ ...form, applies_to_test_types: next });
                      }}
                    />
                    {t}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                Severity minimum
              </Label>
              <Input
                data-testid="preset-severity-minimum"
                value={form.severity_minimum}
                onChange={(e) => setForm({ ...form, severity_minimum: e.target.value })}
                placeholder="optional"
                className="mt-1 h-9 text-sm"
              />
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-slate-700">
            <input
              type="checkbox"
              data-testid="preset-default-for-recommendation"
              checked={form.default_for_recommendation}
              onChange={(e) => setForm({ ...form, default_for_recommendation: e.target.checked })}
            />
            Use as default preset for matching recommendations
          </label>
        </div>
      </div>

      {/* Sticky footer */}
      <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-3 flex items-center justify-between gap-2">
        <div>
          {doc && (
            <Button
              data-testid="preset-toggle-active-btn"
              variant="outline"
              onClick={toggleActive}
              className="rounded-full"
              size="sm"
            >
              {doc.active === false
                ? <><Power className="w-3.5 h-3.5 mr-1" /> Reactivate</>
                : <><PowerOff className="w-3.5 h-3.5 mr-1" /> Deactivate</>
              }
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={onClose} className="rounded-full" size="sm">Close</Button>
          <Button
            data-testid="preset-save-btn"
            onClick={save}
            disabled={saving}
            className="rounded-full bg-cyan-600 hover:bg-cyan-700"
            size="sm"
          >
            {saving ? "Saving…" : (isCreate ? "Create preset" : "Save changes")}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ============================================================ LIST PAGE

export default function AdminProposalPresets() {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | active | inactive
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [activeId, setActiveId] = useState(null); // null | "new" | preset_id
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/admin/proposal-presets"),
      api.get("/admin/products"),
    ]).then(([pres, prods]) => {
      if (cancelled) return;
      setPresets(pres.data || []);
      setProducts(prods.data || []);
      setLoading(false);
    }).catch((e) => {
      if (!cancelled) {
        toast.error(formatApiError(e));
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return presets.filter((p) => {
      if (filter === "active" && p.active === false) return false;
      if (filter === "inactive" && p.active !== false) return false;
      if (q && !(p.name || "").toLowerCase().includes(q)
            && !(p.category || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [presets, filter, search]);

  return (
    <PortalShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="proposal-presets-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-700">Admin</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-1">Proposal Presets</h1>
            <p className="text-sm text-slate-500 mt-2 max-w-2xl">
              Reusable estimate templates. Build a preset once (e.g.
              <strong> Whole Home Filtration — Tank Water Options</strong> with
              T20 / T20UV / TR20UV) and apply it inside any new proposal.
              Existing proposals are never changed when you edit a preset.
            </p>
          </div>
          <Button
            data-testid="new-preset-btn"
            onClick={() => setActiveId("new")}
            className="rounded-full bg-cyan-600 hover:bg-cyan-700"
          >
            <Plus className="w-4 h-4 mr-1" /> New preset
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-slate-200 p-3 mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
            <Input
              data-testid="preset-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or category…"
              className="pl-8 h-9 text-sm"
            />
          </div>
          <div className="flex gap-1">
            {[
              { v: "all",      label: "All" },
              { v: "active",   label: "Active" },
              { v: "inactive", label: "Inactive" },
            ].map((opt) => (
              <button
                key={opt.v}
                data-testid={`preset-filter-${opt.v}`}
                onClick={() => setFilter(opt.v)}
                className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                  filter === opt.v
                    ? "bg-slate-900 text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-12 text-sm text-slate-500">Loading presets…</div>
        ) : filtered.length === 0 ? (
          <div data-testid="proposal-presets-empty" className="text-center py-12 bg-white rounded-2xl border border-dashed border-slate-300">
            <AlertCircle className="w-10 h-10 text-slate-400 mx-auto mb-3" />
            <div className="text-sm text-slate-500">No presets match this filter.</div>
            <button onClick={() => setActiveId("new")} className="mt-3 text-xs font-semibold text-cyan-700 hover:underline">
              Create your first preset →
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
            {filtered.map((p) => {
              const groupCount = (p.option_groups || []).length;
              const lineCount = (p.lines || []).length;
              return (
                <button
                  key={p.id}
                  data-testid={`preset-row-${p.id}`}
                  onClick={() => setActiveId(p.id)}
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center justify-between gap-4 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-slate-900 truncate">{p.name}</div>
                      {p.active === false && (
                        <span className="text-[10px] uppercase font-bold tracking-wider text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-full">
                          Inactive
                        </span>
                      )}
                      {p.category && (
                        <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-full">
                          {p.category}
                        </span>
                      )}
                    </div>
                    {p.description && (
                      <div className="text-xs text-slate-500 mt-0.5 truncate">{p.description}</div>
                    )}
                    <div className="text-[11px] text-slate-400 mt-1">
                      {groupCount} group{groupCount === 1 ? "" : "s"}
                      <span className="mx-1">·</span>
                      {lineCount} line{lineCount === 1 ? "" : "s"}
                      {p.updated_at && <>
                        <span className="mx-1">·</span>
                        Updated {formatDateTimeAU(p.updated_at)}
                      </>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {activeId && (
        <>
          {/* Backdrop */}
          <button
            aria-label="Close drawer"
            onClick={() => setActiveId(null)}
            className="fixed inset-0 z-30 bg-slate-900/40 backdrop-blur-[1px]"
          />
          <PresetDetail
            key={activeId}
            presetId={activeId}
            products={products}
            onClose={() => setActiveId(null)}
            onChanged={(id) => {
              // Phase 2L-A27 Step 2 fix (iteration_42): after a successful
              // CREATE, flip the drawer into edit mode so subsequent Saves
              // PATCH instead of POSTing duplicates.
              if (id && id !== "new") setActiveId(id);
              setReloadKey((k) => k + 1);
            }}
          />
        </>
      )}
    </PortalShell>
  );
}
